<template>
  <span>
    <!-- Botón disparador (opcional, para reutilizar el modal desde otras vistas) -->
    <button
      v-if="show_button"
      type="button"
      :class="`btn btn-sm btn-ai-quick ${button_class}`"
      @click="open"
    >
      <img src="@/assets/icon-ai.svg" alt="" class="icon-btn-ai mr-1" />
      {{ $t("reusable.aiEgressProfileBuilderModal.openButton") }}
    </button>

    <!-- Modal (BS4 manual, sin Bootstrap-Vue) -->
    <div v-if="visible" class="ai-builder-overlay">
      <div class="ai-builder-dialog" role="dialog" aria-modal="true">
        <div class="modal-content ai-builder-content">
          <div class="modal-header py-2">
            <h5 class="modal-title">
              <img src="@/assets/icon-ai.svg" alt="" class="icon-btn-ai mr-1" />
              {{ $t("reusable.aiEgressProfileBuilderModal.title") }}
            </h5>
            <button
              type="button"
              class="close"
              :aria-label="$t('reusable.aiEgressProfileBuilderModal.close')"
              @click="close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body ai-builder-body">
            <!-- Error -->
            <div v-if="error" class="alert alert-danger py-2">
              {{ error }}
            </div>

            <!-- Cargando contexto / generación inicial -->
            <div v-if="loading" class="text-center my-5">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 text-muted">
                {{ $t("reusable.aiEgressProfileBuilderModal.generating") }}
              </p>
            </div>

            <template v-else>
              <!-- Indicador de generación por streaming -->
              <div
                v-if="generating"
                class="ai-builder-generating d-flex align-items-center mb-3"
              >
                <div
                  class="spinner-border spinner-border-sm mr-2"
                  role="status"
                ></div>
                <span>{{
                  $t("reusable.aiEgressProfileBuilderModal.generating")
                }}</span>
              </div>

              <!-- Descriptores -->
              <h6 class="ai-builder-section-title">
                {{ $t("reusable.aiEgressProfileBuilderModal.descriptors") }}
              </h6>
              <p v-if="descriptors.length == 0" class="text-muted small">
                {{
                  $t("reusable.aiEgressProfileBuilderModal.noDescriptors", {
                    entity: $getVisibleNames(
                      "mesh.egressprofile",
                      false,
                      $t(
                        "reusable.aiEgressProfileBuilderModal.egressProfileLabel"
                      ),
                      true
                    ),
                  })
                }}
              </p>
              <div
                v-for="item in descriptors"
                :key="`descriptor-${item.descriptor}`"
                :class="[
                  'card mb-2 ai-builder-card',
                  { 'ai-builder-deleted': item.deleted },
                ]"
              >
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{{ item.title }}</strong>
                      <span
                        v-if="item.existing"
                        class="badge badge-secondary ml-1"
                      >
                        {{
                          $t(
                            "reusable.aiEgressProfileBuilderModal.alreadyExists"
                          )
                        }}
                      </span>
                    </div>
                    <div class="text-nowrap ml-2">
                      <button
                        v-if="!item.deleted"
                        type="button"
                        class="btn btn-sm ai-builder-btn ai-builder-btn-remove"
                        :disabled="busy"
                        @click="item.deleted = true"
                      >
                        <span aria-hidden="true" class="mr-1">&times;</span
                        >{{ $t("reusable.aiEgressProfileBuilderModal.remove") }}
                      </button>
                      <button
                        v-else
                        type="button"
                        class="btn btn-sm ai-builder-btn ai-builder-btn-restore"
                        :disabled="busy"
                        @click="restoreDescriptor(item)"
                      >
                        <span aria-hidden="true" class="mr-1">&#8634;</span
                        >{{
                          $t("reusable.aiEgressProfileBuilderModal.restore")
                        }}
                      </button>
                    </div>
                  </div>

                  <div v-if="item.regenerating" class="text-muted small my-2">
                    <div
                      class="spinner-border spinner-border-sm mr-1"
                      role="status"
                    ></div>
                    {{ $t("reusable.aiEgressProfileBuilderModal.generating") }}
                  </div>
                  <p v-else class="mb-1 mt-1 ai-builder-description">
                    {{ item.description }}
                  </p>

                  <AiThemeInput
                    v-if="!item.deleted"
                    v-model="item.theme"
                    class="mt-1"
                    :placeholder="
                      $t(
                        'reusable.aiEgressProfileBuilderModal.themePlaceholder'
                      )
                    "
                    :hint="
                      $t('reusable.aiEgressProfileBuilderModal.themeHint')
                    "
                    :info="
                      $t('reusable.aiEgressProfileBuilderModal.themeInfo')
                    "
                  >
                    <template #append>
                      <button
                        type="button"
                        class="btn ai-builder-btn-regen"
                        :disabled="busy"
                        @click="regenerateDescriptor(item)"
                      >
                        <span aria-hidden="true" class="mr-1">&#8635;</span
                        >{{
                          $t("reusable.aiEgressProfileBuilderModal.regenerate")
                        }}
                      </button>
                    </template>
                  </AiThemeInput>
                </div>
              </div>

              <!-- Competencias -->
              <h6 class="ai-builder-section-title mt-4">
                {{
                  $getVisibleNames(
                    "mesh.competence",
                    true,
                    $t("reusable.aiEgressProfileBuilderModal.competences")
                  )
                }}
              </h6>
              <div
                v-for="item in competences"
                :key="`competence-${item.uid}`"
                :class="[
                  'card mb-2 ai-builder-card',
                  { 'ai-builder-deleted': item.deleted },
                ]"
              >
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <span
                        v-if="item.existing"
                        class="badge badge-secondary mr-1"
                      >
                        {{
                          $t(
                            "reusable.aiEgressProfileBuilderModal.alreadyExists"
                          )
                        }}
                      </span>
                      <template v-if="item.deleted">
                        <span
                          v-if="typeName(item.type)"
                          class="badge badge-info mr-1"
                          >{{ typeName(item.type) }}</span
                        >
                        <span
                          v-if="cycleName(item.cycle)"
                          class="badge badge-light border mr-1"
                          >{{ cycleName(item.cycle) }}</span
                        >
                      </template>
                    </div>
                    <div class="text-nowrap ml-2">
                      <button
                        v-if="!item.deleted"
                        type="button"
                        class="btn btn-sm ai-builder-btn ai-builder-btn-remove"
                        :disabled="busy"
                        @click="item.deleted = true"
                      >
                        <span aria-hidden="true" class="mr-1">&times;</span
                        >{{ $t("reusable.aiEgressProfileBuilderModal.remove") }}
                      </button>
                      <button
                        v-else
                        type="button"
                        class="btn btn-sm ai-builder-btn ai-builder-btn-restore"
                        :disabled="busy"
                        @click="item.deleted = false"
                      >
                        <span aria-hidden="true" class="mr-1">&#8634;</span
                        >{{
                          $t("reusable.aiEgressProfileBuilderModal.restore")
                        }}
                      </button>
                    </div>
                  </div>

                  <div v-if="item.regenerating" class="text-muted small my-2">
                    <div
                      class="spinner-border spinner-border-sm mr-1"
                      role="status"
                    ></div>
                    {{ $t("reusable.aiEgressProfileBuilderModal.generating") }}
                  </div>
                  <template v-else>
                    <div
                      v-if="
                        !item.deleted &&
                        (competence_types.length > 0 ||
                          egress_profile_cycles.length > 0)
                      "
                      class="d-flex mt-1"
                    >
                      <select
                        v-if="competence_types.length > 0"
                        v-model="item.type"
                        class="form-control form-control-sm mr-1"
                        :disabled="busy"
                      >
                        <option :value="null">
                          {{
                            $t("reusable.aiEgressProfileBuilderModal.noType")
                          }}
                        </option>
                        <option
                          v-for="type in competence_types"
                          :key="`type-${type.id}`"
                          :value="type.id"
                        >
                          {{ type.name }}
                        </option>
                      </select>
                      <select
                        v-if="egress_profile_cycles.length > 0"
                        v-model="item.cycle"
                        class="form-control form-control-sm"
                        :disabled="busy"
                      >
                        <option :value="null">
                          {{
                            $t("reusable.aiEgressProfileBuilderModal.noCycle")
                          }}
                        </option>
                        <option
                          v-for="cycle in egress_profile_cycles"
                          :key="`cycle-${cycle.id}`"
                          :value="cycle.id"
                        >
                          {{ cycle.name }}
                        </option>
                      </select>
                    </div>
                    <p class="mb-1 mt-1 ai-builder-description">
                      {{ item.full_sentence }}
                    </p>
                  </template>

                  <AiThemeInput
                    v-if="!item.deleted"
                    v-model="item.theme"
                    class="mt-1"
                    :placeholder="
                      $t(
                        'reusable.aiEgressProfileBuilderModal.themePlaceholder'
                      )
                    "
                    :hint="
                      $t('reusable.aiEgressProfileBuilderModal.themeHint')
                    "
                    :info="
                      $t('reusable.aiEgressProfileBuilderModal.themeInfo')
                    "
                  >
                    <template #append>
                      <button
                        type="button"
                        class="btn ai-builder-btn-regen"
                        :disabled="busy"
                        @click="regenerateCompetence(item)"
                      >
                        <span aria-hidden="true" class="mr-1">&#8635;</span
                        >{{
                          $t("reusable.aiEgressProfileBuilderModal.regenerate")
                        }}
                      </button>
                    </template>
                  </AiThemeInput>
                </div>
              </div>

              <!-- Añadir competencia -->
              <AiThemeInput
                v-model="new_competence_theme"
                class="mt-2"
                :placeholder="
                  $t('reusable.aiEgressProfileBuilderModal.themePlaceholder')
                "
                :hint="$t('reusable.aiEgressProfileBuilderModal.themeHint')"
                :info="$t('reusable.aiEgressProfileBuilderModal.themeInfo')"
              >
                <template #append>
                  <button
                    type="button"
                    class="btn ai-builder-btn-regen"
                    :disabled="busy || adding_competence"
                    @click="addCompetence"
                  >
                    <span
                      v-if="adding_competence"
                      class="spinner-border spinner-border-sm mr-1"
                      role="status"
                    ></span>
                    <span v-else aria-hidden="true" class="mr-1">+</span>
                    {{
                      $t(
                        "reusable.aiEgressProfileBuilderModal.addCompetence",
                        {
                          entity: $getVisibleNames(
                            "mesh.competence",
                            false,
                            $t(
                              "reusable.aiEgressProfileBuilderModal.competenceLabel"
                            )
                          ),
                        }
                      )
                    }}
                  </button>
                </template>
              </AiThemeInput>
            </template>
          </div>

          <div class="modal-footer py-2">
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              :disabled="saving"
              @click="close"
            >
              {{ $t("reusable.aiEgressProfileBuilderModal.cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              :disabled="busy || loading || !hasPendingItems"
              @click="save"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm mr-1"
                role="status"
              ></span>
              {{ $t("reusable.aiEgressProfileBuilderModal.confirmSave") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </span>
</template>

<script>
import { ref, computed, getCurrentInstance } from "vue";
import * as names from "@/store/names";
import { toast } from "@/utils/utils";
import useAiBuilderStream from "./ai-builder/useAiBuilderStream";
import { composeSentence, deriveActionVerb } from "./ai-builder/sentence";
import AiThemeInput from "./ai-builder/AiThemeInput";

export default {
  name: "AiEgressProfileBuilderModal",
  components: { AiThemeInput },
  props: {
    egress_profile_id: {
      type: Number,
      required: true,
    },
    show_button: {
      type: Boolean,
      default: false,
    },
    button_class: {
      type: String,
      default: "",
    },
  },
  setup(props, { emit }) {
    const proxy = getCurrentInstance().proxy;
    const store = proxy.$store;

    const visible = ref(false);
    const loading = ref(false);
    const generating = ref(false);
    const saving = ref(false);
    const adding_competence = ref(false);
    const error = ref(null);
    const descriptors = ref([]);
    const competences = ref([]);
    const new_competence_theme = ref("");
    let competence_uid = 0;

    const competence_types = computed(
      () => store.getters[names.COMPETENCE_TYPES]
    );
    const cycles = computed(() => store.getters[names.CYCLES]);
    const profile_competences = computed(() =>
      store.getters[names.PROFILE_COMPETENCES].filter(
        (x) => x.egress_profile == props.egress_profile_id
      )
    );
    const stored_competences = computed(
      () => store.getters[names.COMPETENCES]
    );
    const ep_middle_descriptors = computed(() =>
      store.getters[names.EP_MIDDLE_DESCRIPTORS].filter(
        (x) => x.egress_profile == props.egress_profile_id
      )
    );
    // El selector de ciclo solo tiene sentido si el perfil de egreso tiene
    // ciclos formativos asignados (mismo criterio que CompetenceForm.vue).
    const profile_cycles = computed(() =>
      store.getters[names.PROFILE_CYCLES].filter(
        (x) => x.egress_profile == props.egress_profile_id
      )
    );
    const egress_profile_cycles = computed(() => {
      const ids = profile_cycles.value.map((x) => x.cycle);
      return cycles.value.filter((x) => ids.includes(x.id));
    });
    const taxonomies = ref([]);
    const verbs = computed(() => {
      const list = [];
      taxonomies.value.forEach((t) =>
        (t.cognitive_levels || []).forEach((cl) =>
          (cl.verbs || []).forEach((v) => list.push(v))
        )
      );
      return list;
    });

    const busy = computed(
      () =>
        saving.value ||
        generating.value ||
        adding_competence.value ||
        descriptors.value.some((x) => x.regenerating) ||
        competences.value.some((x) => x.regenerating)
    );
    function isDescriptorChanged(item) {
      return item.existing && item.description !== item.original_description;
    }
    function isCompetenceChanged(item) {
      if (!item.existing) return false;
      const original = item.original;
      return (
        item.full_sentence !== original.full_sentence ||
        item.type != original.type ||
        item.cycle != original.cycle
      );
    }
    const hasPendingItems = computed(
      () =>
        descriptors.value.some((x) =>
          x.existing
            ? x.deleted || isDescriptorChanged(x)
            : !x.deleted && x.description
        ) ||
        competences.value.some((x) =>
          x.existing ? x.deleted || isCompetenceChanged(x) : !x.deleted
        )
    );

    function typeName(type_id) {
      if (type_id == null) return "";
      const instance = competence_types.value.find((x) => x.id == type_id);
      return instance ? instance.name : "";
    }
    function cycleName(cycle_id) {
      if (cycle_id == null) return "";
      const instance = cycles.value.find((x) => x.id == cycle_id);
      return instance ? instance.name : "";
    }
    // Las competencias YA EXISTENTES (persistidas) el backend las evita con
    // su propia consulta a la BD (existing_competences, acotada al perfil de
    // egreso); acá solo se envían las propuestas nuevas de esta sesión que
    // aún no se han guardado, para no duplicar el mismo texto dos veces en
    // el prompt y ahorrar tokens.
    function avoidSentences() {
      return competences.value
        .filter((x) => !x.deleted && !x.existing)
        .map((x) => x.full_sentence);
    }
    // Las taxonomías (con sus verbos) solo se necesitan para derivar "verb"
    // al guardar una competencia; no hace falta pedirlas solo para abrir el
    // modal a mirar o eliminar. Se cargan recién al guardar, y una sola vez
    // (el store ya cachea internamente).
    async function ensureTaxonomies() {
      if (taxonomies.value.length > 0) return;
      taxonomies.value = await store.dispatch(names.FETCH_TAXONOMIES);
    }

    // Conversación con el endpoint builder (SSE + manejo de 403):
    // lógica compartida con los demás builders de dominio.
    const { callBuilder } = useAiBuilderStream({
      url: `/common/egress-profile-builder-ai/`,
      generate_error_key: "reusable.aiEgressProfileBuilderModal.generateError",
    });

    function pushGeneratedDescriptor(item) {
      const school_descriptor = store.getters[
        names.EGRESS_PROFILE_DESCRIPTORS
      ].find((x) => x.id == item.descriptor);
      if (school_descriptor == null) return;
      const current = descriptors.value.find(
        (x) => x.descriptor == item.descriptor
      );
      if (current != null) {
        if (!current.existing) current.description = item.description;
        return;
      }
      descriptors.value.push({
        descriptor: school_descriptor.id,
        title: school_descriptor.title,
        description: item.description,
        existing: false,
        deleted: false,
        theme: "",
        regenerating: false,
      });
    }

    function buildExistingCompetences() {
      return profile_competences.value.reduce((list, profile_competence) => {
        const competence = stored_competences.value.find(
          (x) => x.id == profile_competence.competence
        );
        if (competence == null) return list;
        list.push({
          uid: ++competence_uid,
          profile_competence_id: profile_competence.id,
          competence_id: competence.id,
          full_sentence: competence.full_sentence,
          type: competence.type,
          cycle: profile_competence.cycle,
          existing: true,
          deleted: false,
          theme: "",
          regenerating: false,
          original: {
            full_sentence: competence.full_sentence,
            type: competence.type,
            cycle: profile_competence.cycle,
          },
        });
        return list;
      }, []);
    }

    function pushGeneratedCompetence(item) {
      competences.value.push({
        uid: ++competence_uid,
        full_sentence: composeSentence(item),
        type: item.type,
        cycle: item.cycle,
        existing: false,
        deleted: false,
        theme: "",
        regenerating: false,
      });
    }

    function reconcile(data) {
      // El resultado final validado del backend es autoritativo; se
      // preservan las marcas del usuario (deleted/theme) hechas durante
      // el streaming.
      const generated = data.descriptors || [];
      const prev_descriptors = descriptors.value;
      descriptors.value = store.getters[
        names.EGRESS_PROFILE_DESCRIPTORS
      ].reduce((list, descriptor) => {
        const middle = ep_middle_descriptors.value.find(
          (x) =>
            x.descriptor == descriptor.id &&
            x.description != null &&
            x.description.trim() != ""
        );
        const ai_descriptor = generated.find(
          (x) => x.descriptor == descriptor.id
        );
        if (middle == null && ai_descriptor == null) return list;
        const prev = prev_descriptors.find(
          (x) => x.descriptor == descriptor.id
        );
        // Los existentes pueden venir con HTML del editor de texto: se
        // muestran y editan como texto plano.
        const description = middle
          ? proxy.$htmlToPlainText(middle.description)
          : ai_descriptor.description;
        list.push({
          descriptor: descriptor.id,
          title: descriptor.title,
          description,
          existing: middle != null,
          middle_id: middle ? middle.id : null,
          original_description: middle ? description : null,
          deleted: prev ? prev.deleted : false,
          theme: prev ? prev.theme : "",
          regenerating: false,
        });
        return list;
      }, []);
      // Las competencias ya existentes no vienen en la respuesta de la IA
      // (esta solo propone competencias nuevas): se preservan tal cual.
      const existing_part = competences.value.filter((x) => x.existing);
      const prev_generated = competences.value.filter((x) => !x.existing);
      const new_generated = (data.competences || []).map((x, index) => {
        const prev = prev_generated[index];
        return {
          uid: prev ? prev.uid : ++competence_uid,
          full_sentence: composeSentence(x),
          type: x.type,
          cycle: x.cycle,
          existing: false,
          deleted: prev ? prev.deleted : false,
          theme: prev ? prev.theme : "",
          regenerating: false,
        };
      });
      competences.value = existing_part.concat(new_generated);
    }

    async function generateAll() {
      // Los descriptores con contenido ya existente se muestran de
      // inmediato, tal cual (solo lectura); el resto llega por streaming.
      descriptors.value = store.getters[
        names.EGRESS_PROFILE_DESCRIPTORS
      ].reduce((list, descriptor) => {
        const middle = ep_middle_descriptors.value.find(
          (x) =>
            x.descriptor == descriptor.id &&
            x.description != null &&
            x.description.trim() != ""
        );
        if (middle == null) return list;
        const description = proxy.$htmlToPlainText(middle.description);
        list.push({
          descriptor: descriptor.id,
          title: descriptor.title,
          description,
          existing: true,
          middle_id: middle.id,
          original_description: description,
          deleted: false,
          theme: "",
          regenerating: false,
        });
        return list;
      }, []);
      // Las competencias ya creadas se muestran de inmediato, sin esperar a
      // la IA; esta solo propondrá competencias adicionales.
      competences.value = buildExistingCompetences();
      const data = await callBuilder(
        {
          mode: "all",
          egress_profile: props.egress_profile_id,
        },
        (event) => {
          if (event.type === "descriptor") pushGeneratedDescriptor(event.item);
          else if (event.type === "competence")
            pushGeneratedCompetence(event.item);
        }
      );
      reconcile(data);
    }

    async function open() {
      visible.value = true;
      error.value = null;
      loading.value = true;
      descriptors.value = [];
      competences.value = [];
      try {
        const fetches = [
          store.dispatch(names.FETCH_EP_MIDDLE_DESCRIPTORS, {
            egress_profile_id: props.egress_profile_id,
          }),
          store.dispatch(names.FETCH_CYCLES),
          store.dispatch(names.FETCH_PROFILE_COMPETENCES, {
            egress_profile_id: props.egress_profile_id,
          }),
          store.dispatch(names.FETCH_COMPETENCES, props.egress_profile_id),
          store.dispatch(names.FETCH_PROFILE_CYCLES, {
            egress_profile_id: props.egress_profile_id,
          }),
        ];
        // Descriptores y tipos de competencia son catálogos de la
        // institución (no tienen relación con un perfil de egreso
        // puntual): si ya están cargados en el store, no hace falta
        // volver a pedirlos cada vez que se abre el modal.
        if (store.getters[names.EGRESS_PROFILE_DESCRIPTORS].length === 0) {
          fetches.push(store.dispatch(names.FETCH_PROFILE_DESCRIPTORS));
        }
        if (competence_types.value.length === 0) {
          fetches.push(store.dispatch(names.FETCH_COMPETENCE_TYPES));
        }
        await Promise.all(fetches);
        // El contenido llega por streaming: se muestra a medida que se
        // genera, con un indicador liviano en vez del spinner de pantalla.
        loading.value = false;
        generating.value = true;
        await generateAll();
      } catch (e) {
        error.value =
          e.message ||
          proxy.$t("reusable.aiEgressProfileBuilderModal.generateError");
      } finally {
        loading.value = false;
        generating.value = false;
      }
    }

    function close() {
      if (saving.value) return;
      visible.value = false;
      emit("closed");
    }

    function restoreDescriptor(item) {
      item.deleted = false;
      // Al volver a añadir un descriptor sin contenido, se regenera.
      if (!item.description) regenerateDescriptor(item);
    }

    async function regenerateDescriptor(item) {
      item.regenerating = true;
      error.value = null;
      try {
        const data = await callBuilder({
          mode: "descriptor",
          egress_profile: props.egress_profile_id,
          descriptor: item.descriptor,
          theme: item.theme,
        });
        const generated = (data.descriptors || []).find(
          (x) => x.descriptor == item.descriptor
        );
        if (generated) {
          item.description = generated.description;
          item.deleted = false;
        }
      } catch (e) {
        error.value = e.message;
      } finally {
        item.regenerating = false;
      }
    }

    async function regenerateCompetence(item) {
      item.regenerating = true;
      error.value = null;
      try {
        const data = await callBuilder({
          mode: "competence",
          egress_profile: props.egress_profile_id,
          theme: item.theme,
          avoid_sentences: avoidSentences(),
        });
        const generated = (data.competences || [])[0];
        if (generated) {
          item.full_sentence = composeSentence(generated);
          if (generated.type != null) item.type = generated.type;
          if (generated.cycle != null) item.cycle = generated.cycle;
        }
      } catch (e) {
        error.value = e.message;
      } finally {
        item.regenerating = false;
      }
    }

    async function addCompetence() {
      adding_competence.value = true;
      error.value = null;
      try {
        const data = await callBuilder({
          mode: "competence",
          egress_profile: props.egress_profile_id,
          theme: new_competence_theme.value,
          avoid_sentences: avoidSentences(),
        });
        const generated = (data.competences || [])[0];
        if (generated) {
          competences.value.push({
            uid: ++competence_uid,
            full_sentence: composeSentence(generated),
            type: generated.type,
            cycle: generated.cycle,
            existing: false,
            deleted: false,
            theme: "",
            regenerating: false,
          });
          new_competence_theme.value = "";
        }
      } catch (e) {
        error.value = e.message;
      } finally {
        adding_competence.value = false;
      }
    }

    async function save() {
      saving.value = true;
      error.value = null;
      try {
        let descriptor_order = ep_middle_descriptors.value.length;
        for (const item of descriptors.value) {
          if (item.existing) {
            if (item.deleted) {
              await store.dispatch(
                names.DELETE_EP_MIDDLE_DESCRIPTORS,
                item.middle_id
              );
            } else if (isDescriptorChanged(item)) {
              await store.dispatch(names.PATCH_EP_MIDDLE_DESCRIPTORS, {
                id: item.middle_id,
                item: { description: item.description },
              });
            }
            continue;
          }
          if (item.deleted || !item.description) continue;
          descriptor_order += 1;
          await store.dispatch(names.POST_EP_MIDDLE_DESCRIPTORS, {
            egress_profile: props.egress_profile_id,
            descriptor: item.descriptor,
            description: item.description,
            order: descriptor_order,
            is_visible: true,
          });
        }
        const needs_verb_derivation = competences.value.some(
          (item) =>
            !item.deleted && (item.existing ? isCompetenceChanged(item) : true)
        );
        if (needs_verb_derivation) await ensureTaxonomies();
        let competence_order = profile_competences.value.reduce(
          (max, x) => Math.max(max, x.order || 0),
          0
        );
        for (const item of competences.value) {
          if (item.existing) {
            if (item.deleted) {
              await store.dispatch(names.DELETE_COMPETENCE, item.competence_id);
              continue;
            }
            if (!isCompetenceChanged(item)) continue;
            // Se guarda como oración única (modo "Insertar" de
            // CompetenceForm): action/verb se derivan del texto, y
            // content/condition/context quedan vacíos.
            const { action, verb } = deriveActionVerb(item.full_sentence, verbs.value);
            await store.dispatch(names.PATCH_COMPETENCE, {
              competence_id: item.competence_id,
              item: {
                action,
                verb,
                content: "",
                condition: "",
                context: "",
                full_sentence: item.full_sentence,
                type: item.type,
              },
            });
            if (item.cycle != item.original.cycle) {
              await store.dispatch(names.PATCH_PROFILE_COMPETENCE, {
                profile_competence_id: item.profile_competence_id,
                item: { cycle: item.cycle },
              });
            }
            continue;
          }
          if (item.deleted) continue;
          competence_order += 1;
          const { action, verb } = deriveActionVerb(item.full_sentence, verbs.value);
          const competence = await store.dispatch(names.POST_COMPETENCE, {
            action,
            verb,
            content: "",
            condition: "",
            context: "",
            full_sentence: item.full_sentence,
            type: item.type,
          });
          await store.dispatch(names.POST_PROFILE_COMPETENCE, {
            order: competence_order,
            egress_profile: props.egress_profile_id,
            competence: competence.id,
            cycle: item.cycle,
            capacities: [],
          });
        }
        toast(proxy.$t("reusable.aiEgressProfileBuilderModal.savedToast"));
        emit("saved");
        visible.value = false;
        emit("closed");
      } catch (e) {
        error.value =
          e.message ||
          proxy.$t("reusable.aiEgressProfileBuilderModal.saveError");
      } finally {
        saving.value = false;
      }
    }

    return {
      visible,
      loading,
      generating,
      saving,
      adding_competence,
      error,
      descriptors,
      competences,
      new_competence_theme,
      busy,
      hasPendingItems,
      competence_types,
      egress_profile_cycles,
      typeName,
      cycleName,
      open,
      close,
      restoreDescriptor,
      regenerateDescriptor,
      regenerateCompetence,
      addCompetence,
      save,
    };
  },
};
</script>

<style scoped src="./ai-builder/ai-builder.css"></style>
